import axios from 'axios';
import { GET_ERRORS, SET_CURRENT_USER, SET_PROFILE, SET_MENU_OPTIONS, SET_CONSTANTS, SCREEN_LOCK, RESET_HELP,
         RESET_SECURITY, RESET_AUTH, RESET_NAVMENU, RESET_PROFILE, RESET_OPTIONMENU, BLUR_SCREEN, RESET_DEVICES,
         RESET_NOTIFICATION, CLEAR_LIST } from './types';
import setAuthToken from '../setAuthToken';
import { jwtDecode } from 'jwt-decode';
import { DeviceHelper } from '../helpers/deviceHelper';
import common from '../jsons/common.json';
import languageHelper from '../helpers/languageHelper';
import { db } from '../db';
import queryClient from '../queryClient';
import { persistor } from '../store';
import { setProfileTimezone } from '../components/UI/DateDisplay/dates';

export const registerUser = (user, navigate) => dispatch => {
    axios.post('/users/register', user)
        // Note this was previously `.then(history.push(...))`, which ran the navigation
        // immediately rather than on success. It now fires only once the post resolves.
        .then(() => navigate('/login'))
        .catch(err => {
            dispatch({
                type: GET_ERRORS,
                payload: err.response.data
            });
        });
};

export const loginToTenant = (tenant, url) => async dispatch => {
    db.cache.clear();
    queryClient.clear();
    try{
        let res = await axios.post('/users/logintotenant', {tenant: tenant});
        sessionStorage.setItem('jwtToken', res.data.token);
        setAuthToken(res.data.token);
        const decoded = jwtDecode(res.data.token);
        sessionStorage.setItem('user', JSON.stringify(decoded));
        sessionStorage.setItem('userid', decoded.id);
        dispatch({type: SET_CONSTANTS, payload: {constants: res.data.json, screenDefs: res.data.screenDefinitions}});
        try {
            let profile = await axios.get('/users/me/' + decoded.id);
            let locale = languageHelper.getBrowserLanguage();
            const csvSeparator = languageHelper.getLanguageCsvSeparator(locale);
            sessionStorage.setItem('csvSeparator', csvSeparator);
            sessionStorage.setItem('profile', JSON.stringify(profile.data));
            dispatch(setUserProfile(profile.data));
            dispatch(setCurrentUser(decoded, null));
            dispatch({type: SCREEN_LOCK, payload: {screenLock: false}});
            dispatch({type: BLUR_SCREEN, payload: {blurScreen: false}});
            dispatch({ type: SET_MENU_OPTIONS, payload: JSON.stringify(res.data.screens) });
            let printers = await DeviceHelper.getDevices();
            if(printers.hasDevices) {
                dispatch({ type: 'SET_ZEBRA_PRINTERS', payload: {printers}});
            }   
            dispatch({ type: 'HAS_DEVICES', payload: printers.hasRegisteredDevices });
            if(url){
                window.location.href = url;
            }
        }catch(err){
            dispatch({ type: 'CREATE_ALERT', payload: {message: err.message, title: 'Error', severity: 'error'}});
        }
    } catch(err){
        dispatch({
            type: GET_ERRORS,
            payload: err.response != null && err.response.data != null ? err.response.data : ''
        });
    }
};

export const impersonateUser = (tenant, userId, url) => async dispatch => {
    db.cache.clear();
    queryClient.clear();
    try{
        let res = await axios.post('/int/impersonateuser', {tenant: tenant, userId: userId});
        sessionStorage.setItem('jwtToken', res.data.token);
        setAuthToken(res.data.token);
        const decoded = jwtDecode(res.data.token);
        sessionStorage.setItem('user', JSON.stringify(decoded));
        sessionStorage.setItem('userid', decoded.id);
        dispatch({type: SET_CONSTANTS, payload: {constants: res.data.json, screenDefs: res.data.screenDefinitions}});
        try {
            let profile = await axios.get('/users/me/' + decoded.id);
            let locale = languageHelper.getBrowserLanguage();
            const csvSeparator = languageHelper.getLanguageCsvSeparator(locale);
            sessionStorage.setItem('csvSeparator', csvSeparator);
            sessionStorage.setItem('profile', JSON.stringify(profile.data));
            dispatch(setUserProfile(profile.data));
            dispatch(setCurrentUser(decoded, null));
            dispatch({type: SCREEN_LOCK, payload: {screenLock: false}});
            dispatch({type: BLUR_SCREEN, payload: {blurScreen: false}});
            dispatch({ type: SET_MENU_OPTIONS, payload: JSON.stringify(res.data.screens) });
            let printers = await DeviceHelper.getDevices();
            if(printers.hasDevices) {
                dispatch({ type: 'SET_ZEBRA_PRINTERS', payload: {printers}});
            }
            dispatch({ type: 'HAS_DEVICES', payload: printers.hasRegisteredDevices });
            if(url){
                window.location.href = url;
            }
        }catch(err){
            dispatch({ type: 'CREATE_ALERT', payload: {message: err.message, title: 'Error', severity: 'error'}});
        }
    } catch(err){
        dispatch({
            type: GET_ERRORS,
            payload: err.response != null && err.response.data != null ? err.response.data : ''
        });
    }
};

export const loginAsAdmin = (msal, token, userid) => async dispatch => {
    try {
        const response = await axios.post('/users/lam', 
            { userid },
            { headers: { 'Authorization': `Bearer ${token}` }}
        );
        
        if (response.data.success) {
            sessionStorage.setItem('jwtToken', response.data.token);
            setAuthToken(response.data.token);
            const decoded = jwtDecode(response.data.token);
            sessionStorage.setItem('user', JSON.stringify(decoded));
            sessionStorage.setItem('userid', decoded.id);
            dispatch(setCurrentUser(decoded, null));
            dispatch({type: SET_CONSTANTS, payload: {constants: {}, screenDefs: response.data.screenDefinitions}});
        } else {
            dispatch({ type: 'CREATE_ALERT', payload: {message: response.data.error || 'Login failed', title: 'Error', severity: 'error'}});
        }
    } catch (err) {
        console.error('Login as admin failed:', err);
        const message = err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : 'Login failed. Please try again.';
        dispatch({ type: 'CREATE_ALERT', payload: {message, title: 'Error', severity: 'error'}});
    }
};

export const loginUser = (screens, screenDefs, token, json, from) => async dispatch => {
    try {
        sessionStorage.setItem('jwtToken', token);
        setAuthToken(token);
        const decoded = jwtDecode(token);
        sessionStorage.setItem('user', JSON.stringify(decoded));
        sessionStorage.setItem('userid', decoded.id);
        dispatch({type: SET_CONSTANTS, payload: {constants: json, screenDefs: screenDefs}});
        try {
            let profile = await axios.get('/users/me/' + decoded.id);
            let locale = languageHelper.getBrowserLanguage();
            const csvSeparator = languageHelper.getLanguageCsvSeparator(locale);
            sessionStorage.setItem('csvSeparator', csvSeparator);
            sessionStorage.setItem('profile', JSON.stringify(profile.data));
            dispatch(setUserProfile(profile.data));
            dispatch(setCurrentUser(decoded, from));
            dispatch({type: SCREEN_LOCK, payload: {screenLock: false}});
            dispatch({type: BLUR_SCREEN, payload: {blurScreen: false}});
            dispatch({ type: SET_MENU_OPTIONS, payload: JSON.stringify(screens) });
            let printers = await DeviceHelper.getDevices();
            if(printers.hasDevices) {
                dispatch({ type: 'SET_ZEBRA_PRINTERS', payload: {printers}});
            }
            dispatch({ type: 'HAS_DEVICES', payload: printers.hasRegisteredDevices });
        }catch(err){
            dispatch({ type: 'CREATE_ALERT', payload: {message: err.message, title: 'Error', severity: 'error'}});
        };
    } catch(err){
        dispatch({
            type: GET_ERRORS,
            payload: err.response != null && err.response.data != null ? err.response.data : ''
        });
    }
};

export const loginAzureUser = (msal, accessToken, userid, tenant, from, authType) => async dispatch => {
    sessionStorage.setItem('msal', msal);
    sessionStorage.setItem('azureToken', accessToken);
    let locale = languageHelper.getBrowserLanguage();
    const csvSeparator = languageHelper.getLanguageCsvSeparator(locale);
    sessionStorage.setItem('csvSeparator', csvSeparator);
    setAuthToken('Bearer ' + accessToken);
    try {
        let result = null;
        if(authType === common.auth.azure){
            result = await axios.post('/users/userdetails', {userid: userid, tenant});
        } else {
            result = await axios.post('/users/googleLogin', {userid: userid, tenant});
        }
        if(result.data.token != null){
            sessionStorage.setItem('jwtToken', result.data.token);
            setAuthToken(result.data.token);
            const decoded = jwtDecode(result.data.token);
            let profile = await axios.get('/users/me/' + decoded.id);
            sessionStorage.setItem('profile', JSON.stringify(profile.data));
            sessionStorage.setItem('user', JSON.stringify(decoded));
            sessionStorage.setItem('userid', decoded.id);
            dispatch(setUserProfile(profile.data));
            dispatch({type: SET_CONSTANTS, payload: {constants: result.data.json, screenDefs: result.data.screenDefinitions}});
            dispatch({type: SCREEN_LOCK, payload: {screenLock: false}});
            dispatch({type: BLUR_SCREEN, payload: {blurScreen: false}});
            dispatch(setCurrentUser(decoded, from));
            dispatch({ type: SET_MENU_OPTIONS, payload: JSON.stringify(result.data.screens) });
            let printers = await DeviceHelper.getDevices();
            if(printers.hasDevices) {
                dispatch({ type: 'SET_ZEBRA_PRINTERS', payload: {printers}});
            }
            dispatch({ type: 'HAS_DEVICES', payload: printers.hasRegisteredDevices });
        } else {
            dispatch({ type: 'CREATE_ALERT', payload: {message: result, title: 'Error', severity: 'error'}});
        }
    } catch(err){
        dispatch({ type: 'CREATE_ALERT', payload: {message: err, title: 'Error', severity: 'error'}});
    }
};

export const setCurrentUser = (decoded, referralLink) => {
    setProfileTimezone(decoded != null ? decoded.timezone : null);
    return{
        type: SET_CURRENT_USER,
        payload: {user: decoded, referralLink: referralLink}
    }
};

export const setUserProfile = profile => {
    return {
        type: SET_PROFILE,
        payload: profile
    }
};

export const logoutUser = () => async dispatch => {
    try {
        await axios.get('/api/tenant/logout');
    } catch (err) {
        console.error('Error during logout:', err);
    }
    sessionStorage.clear();
    localStorage.clear();
    db.cache.clear();
    queryClient.clear();
    setAuthToken(false);
    dispatch({ type: SCREEN_LOCK, payload: { screenLock: false } });
    dispatch({ type: BLUR_SCREEN, payload: { blurScreen: false } });
    dispatch(setCurrentUser({}));
    dispatch(setUserProfile({}));
    dispatch({ type: RESET_DEVICES, payload: null });
    dispatch({ type: RESET_OPTIONMENU, payload: null });
    dispatch({ type: RESET_SECURITY, payload: null });
    dispatch({ type: RESET_AUTH, payload: null });
    dispatch({ type: RESET_NAVMENU, payload: null });
    dispatch({ type: RESET_PROFILE, payload: null });
    dispatch({ type: RESET_HELP, payload: null });
};

// Silent re-auth for the "Update available" flow. Mints a fresh JWT in the
// user's current context (tenant or admin), rebuilds profile/constants/screens
// from the server, then hard-reloads to pick up the new frontend bundle and
// land back on the route the user was already on. No password prompt.
export const refreshSession = () => async dispatch => {
    const route = window.location.hash || '#/home';
    try {
        const res = await axios.post('/users/refreshSession');
        if (!res.data?.success || !res.data.token) {
            console.error('refreshSession returned no token; falling back to login');
            sessionStorage.clear();
            localStorage.clear();
            setAuthToken(false);
            window.location.href = '/#/login';
            return;
        }
        // Drop caches and the stale profile so they rebuild against the fresh token
        db.cache.clear();
        queryClient.clear();
        sessionStorage.removeItem('profile');

        const newToken = res.data.token;
        sessionStorage.setItem('jwtToken', newToken);
        setAuthToken(newToken);
        const decoded = jwtDecode(newToken);
        sessionStorage.setItem('user', JSON.stringify(decoded));
        sessionStorage.setItem('userid', decoded.id);

        try {
            const profile = await axios.get('/users/me/' + decoded.id);
            const locale = languageHelper.getBrowserLanguage();
            sessionStorage.setItem('csvSeparator', languageHelper.getLanguageCsvSeparator(locale));
            sessionStorage.setItem('profile', JSON.stringify(profile.data));
            dispatch(setUserProfile(profile.data));
        } catch (err) {
            console.warn('refreshSession: profile reload failed', err);
        }

        dispatch(setCurrentUser(decoded, null));
        dispatch({
            type: SET_CONSTANTS,
            payload: {
                constants: res.data.json ?? {},
                screenDefs: res.data.screenDefinitions ?? {}
            }
        });
        if (res.data.screens) {
            dispatch({ type: SET_MENU_OPTIONS, payload: JSON.stringify(res.data.screens) });
        }
        dispatch({ type: SCREEN_LOCK, payload: { screenLock: false } });
        dispatch({ type: BLUR_SCREEN, payload: { blurScreen: false } });

        // Hard reload to pick up the new frontend bundle, returning to the same route
        window.location.href = window.location.origin + window.location.pathname + route;
        window.location.reload();
    } catch (err) {
        console.error('refreshSession failed:', err);
        sessionStorage.clear();
        localStorage.clear();
        setAuthToken(false);
        window.location.href = '/#/login';
    }
};

// Wipes every client-side cache that can hold another tenant's data. Used by the
// tenant switcher — a switch has to look like a fresh login, not a page refresh.
//
// What has to go, and why:
//   sessionStorage  – jwtToken/user/userid/profile plus screen-level blobs
//                     (recalls, losses, exciseCounts, apiLogs, ...) AND the
//                     redux-persist blob 'persist:evora-session-storage', which
//                     carries auth/profile/security/navMenu/help/adminList/devices.
//   localStorage    – MaterialReactTable grid state (filters, column visibility,
//                     sorting, pagination) and AutoCompleteFuzzy "recents"; none
//                     of these keys are tenant-scoped, so filters set against
//                     tenant A's values leak onto tenant B's grids.
//   db.cache        – Dexie 'evora'.cache. Mostly tenant-suffixed keys, but not
//                     all of them, and IndexedDB survives a sessionStorage wipe.
//   queryClient     – in-memory react-query cache.
//
// db.lists is deliberately left alone: listHelper keys it by name + tenant, so
// per-tenant grid preferences survive the switch, which is what we want.
//
// sessionStorage.clear() takes the persist blob with it; every whitelisted slice
// is then reset below, so redux-persist rewrites the blob from the incoming
// tenant's state on the next write. switchTenant flushes that write before it
// reloads, which is what makes the post-reload rehydrate land on clean state.
const clearTenantState = async dispatch => {
    sessionStorage.clear();
    localStorage.clear();
    try {
        await db.cache.clear();
    } catch (err) {
        console.warn('cache clear failed', err);
    }
    queryClient.clear();

    dispatch(setCurrentUser({}));
    dispatch(setUserProfile({}));
    dispatch({ type: RESET_DEVICES, payload: null });
    dispatch({ type: RESET_OPTIONMENU, payload: null });
    dispatch({ type: RESET_SECURITY, payload: null });
    dispatch({ type: RESET_AUTH, payload: null });
    dispatch({ type: RESET_NAVMENU, payload: null });
    dispatch({ type: RESET_PROFILE, payload: null });
    dispatch({ type: RESET_HELP, payload: null });
    dispatch({ type: RESET_NOTIFICATION, payload: null });
    dispatch({ type: CLEAR_LIST, payload: null });
};

// Switches the session to another tenant the user belongs to. Mints a new JWT,
// drops all of the outgoing tenant's client state, rebuilds constants/screens/
// profile for the incoming tenant, then hard-reloads onto its landing route.
export const switchTenant = (tenantId, partner = false) => async dispatch => {
    const res = await axios.post('/users/switchtenant', { tenant: tenantId, partner: partner });
    if (!res.data?.success || !res.data.token) {
        throw new Error('switchtenant returned no token');
    }

    await clearTenantState(dispatch);

    const token = res.data.token;
    sessionStorage.setItem('jwtToken', token);
    setAuthToken(token);
    const decoded = jwtDecode(token);
    sessionStorage.setItem('user', JSON.stringify(decoded));
    sessionStorage.setItem('userid', decoded.id);

    try {
        const profile = await axios.get('/users/me/' + decoded.id);
        const locale = languageHelper.getBrowserLanguage();
        sessionStorage.setItem('csvSeparator', languageHelper.getLanguageCsvSeparator(locale));
        sessionStorage.setItem('profile', JSON.stringify(profile.data));
        dispatch(setUserProfile(profile.data));
    } catch (err) {
        console.warn('switchTenant: profile reload failed', err);
    }

    dispatch(setCurrentUser(decoded, null));
    dispatch({
        type: SET_CONSTANTS,
        payload: {
            constants: res.data.json ?? {},
            screenDefs: res.data.screenDefinitions ?? {}
        }
    });
    if (res.data.screens) {
        dispatch({ type: SET_MENU_OPTIONS, payload: JSON.stringify(res.data.screens) });
    }
    dispatch({ type: SCREEN_LOCK, payload: { screenLock: false } });
    dispatch({ type: BLUR_SCREEN, payload: { blurScreen: false } });

    // Make sure the rebuilt state is written before the reload throws it away
    try {
        await persistor.flush();
    } catch (err) {
        console.warn('persist flush failed', err);
    }

    const route = res.data.partner ? '#/portal/home' : '#/home';
    window.location.href = window.location.origin + window.location.pathname + route;
    window.location.reload();
};

export const exitTenant = () => async dispatch => {
    try {
        const response = await axios.post('/users/exitTenant');
        if (response.data.success) {
            // Clear state after successful API call
            db.cache.clear();
            queryClient.clear();
            dispatch(setUserProfile({}));
            dispatch({ type: RESET_DEVICES, payload: null });
            dispatch({ type: RESET_OPTIONMENU, payload: null });
            dispatch({ type: RESET_SECURITY, payload: null });
            dispatch({ type: RESET_AUTH, payload: null });
            dispatch({ type: RESET_NAVMENU, payload: null });
            dispatch({ type: RESET_PROFILE, payload: null });
            dispatch({ type: RESET_HELP, payload: null });

            const decoded = jwtDecode(response.data.token);
            sessionStorage.setItem('jwtToken', response.data.token);
            setAuthToken(response.data.token);
            dispatch(setCurrentUser(decoded, null));
            dispatch({type: SET_CONSTANTS, payload: {constants: {}, screenDefs: response.data.screenDefinitions}});
            window.location.href = '/#/admin';
        }
    } catch (err) {
        console.error('Exit tenant failed:', err);
    }
};